import { ArrowDownIcon } from "@radix-ui/react-icons";
import { useContext, useEffect, useState } from "react";
import {
  ApiError,
  OrganizationSettingsResponse,
  OrganizationsService,
  // StudentLimitedResponse,
  // StudentsService,
  // TutorLimitedResponse,
  // TutorsService,
} from "client/openapi";
import Notifications from "util/notifications";
import { errorText } from "components/Forms/formsApi";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { announceOrganizationRenamed } from "util/organizationRenamed";
import { PageStatus } from "types";
import { DialogActions, DialogClose, DialogContent } from "components/Dialog";
import { Button } from "components/Button";
import { ButtonColor } from "components/Button";
import {
  DefaultPaymentPlanFields,
  useDefaultPaymentPlan,
} from "components/Forms/DefaultPaymentPlanSetting";
import {
  COMPACT_INLINE_NUMBER,
  COMPACT_INPUT,
  CompactRadio,
  FieldError,
} from "components/Inputs/compact";
import TabNavbar from "components/TabNavbar";
import {
  GoBell,
  GoBook,
  GoCalendar,
  GoCommentDiscussion,
  GoCreditCard,
  GoGear,
  GoGlobe,
  GoLocation,
  GoMail,
} from "react-icons/go";
import BookingPagesSettings, { useBookingPages } from "./BookingPagesSettings";
import EmailAddressSettings from "./EmailAddressSettings";
import EmailBrandSettings, { EMAIL_BRAND_TAB } from "./EmailBrandSettings";
import LocationSettings from "./LocationSettings";
import MessagingSettings, { useReplyPrompt } from "./MessagingSettings";
import NotificationSettings, {
  useNotificationPrefs,
} from "./NotificationSettings";
import {
  GROUP_LABEL,
  SECTION,
  SECTION_TEXT,
  SECTION_TITLE,
  wholeNumberError,
} from "./settingsStyles";
import SubjectSettings from "./SubjectSettings";
import "./index.css";

/** One category of settings per tab; General holds the name and the rest. */
const TABS = [
  { value: "General", icon: GoGear },
  { value: "Scheduling", icon: GoCalendar },
  { value: "Locations", icon: GoLocation },
  { value: "Billing", icon: GoCreditCard },
  { value: "Subjects", icon: GoBook },
  { value: "Booking pages", icon: GoGlobe },
  { value: "Notifications", icon: GoBell },
  { value: "Messaging", icon: GoCommentDiscussion },
  { value: EMAIL_BRAND_TAB, icon: GoMail },
];

// Tabs that save as they go (row by row, or as they change): no Save or
// Cancel of their own.
const ROW_TABS = ["Locations", "Subjects", EMAIL_BRAND_TAB];

// The organizations.name column's length.
const MAX_NAME = 100;

const MIN_GRACE_DAYS = 1;
const MAX_GRACE_DAYS = 14;

/** A yes-or-no setting: the question on the left, Yes and No on the right. */
function YesNoRow({
  name,
  label,
  value,
  onChange,
}: {
  name: string;
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-6 py-2.5">
      <span className="text-sm">{label}</span>
      <div className="flex shrink-0 gap-4">
        <CompactRadio
          id={`${name}-yes`}
          name={name}
          checked={value}
          onChange={() => onChange(true)}
        >
          Yes
        </CompactRadio>
        <CompactRadio
          id={`${name}-no`}
          name={name}
          checked={!value}
          onChange={() => onChange(false)}
        >
          No
        </CompactRadio>
      </div>
    </div>
  );
}

export default function OrganizationSettings({
  orgSettings,
  open,
  requestedTab,
}: {
  orgSettings: OrganizationSettingsResponse;
  /** The dialog is open (it stays mounted while closed). */
  open: boolean;
  /** A page asked for one of the tabs (n counts the asks). */
  requestedTab?: { tab: string; n: number };
}) {
  const { currently_selected_organization } = useContext(
    OrgRolesAndAccountContext
  );

  // The settings as last saved: Save turns on when the form differs from
  // them, and closing goes back to them.
  const [saved, setSaved] = useState(orgSettings);
  const [error, setError] = useState<ApiError>();
  const [status, setStatus] = useState<PageStatus>(PageStatus.SUCCESS);
  const [tab, setTab] = useState(requestedTab?.tab ?? TABS[0].value);
  // A page's link to these settings opens them on its tab.
  useEffect(() => {
    if (requestedTab) setTab(requestedTab.tab);
  }, [requestedTab]);

  const [tutorsCanViewUnassignedStudents, setTutorsCanViewUnassignedStudents] =
    useState<boolean>(orgSettings.tutors_can_view_unassigned_students);
  const [
    autoReviewAndApproveMeetingsForAdmin,
    setAutoReviewAndApproveMeetingsForAdmin,
  ] = useState<boolean>(orgSettings.auto_review_and_approve_meetings_for_admin);
  const [
    autoReviewAndApproveMeetingsForTutors,
    setAutoReviewAndApproveMeetingsForTutors,
  ] = useState<boolean>(
    orgSettings.auto_review_and_approve_meetings_for_tutors
  );
  const [
    requireTutorsToAcceptMeetingsBookedByAdmins,
    setRequireTutorsToAcceptMeetingsBookedByAdmins,
  ] = useState<boolean>(
    orgSettings.require_tutors_to_accept_meetings_booked_by_admins
  );
  const [
    requireTutorsToAcceptMeetingsBookedByTutors,
    setRequireTutorsToAcceptMeetingsBookedByTutors,
  ] = useState<boolean>(
    orgSettings.require_tutors_to_accept_meetings_booked_by_tutors
  );
  const [
    requireTutorsToAcceptMeetingsBookedByStudentsOrParents,
    setRequireTutorsToAcceptMeetingsBookedByStudentsOrParents,
  ] = useState<boolean>(
    orgSettings.require_tutors_to_accept_meetings_booked_by_students_or_parents
  );
  // Text until it is a whole number in range; Save waits for that.
  const [gracePeriodInput, setGracePeriodInput] = useState(
    String(
      orgSettings.grace_period_for_editing_meetings_before_billing_and_payroll
    )
  );
  const gracePeriodError = wholeNumberError(
    gracePeriodInput,
    MIN_GRACE_DAYS,
    MAX_GRACE_DAYS
  );
  const [
    sendPendingApprovalReminderEmails,
    setSendPendingApprovalReminderEmails,
  ] = useState<boolean>(orgSettings.send_pending_approval_reminder_emails);
  const [hideNamesInAvailabilityGrids, setHideNamesInAvailabilityGrids] =
    useState<boolean>(orgSettings.hide_names_in_availability_grids);

  // The organization's name as last saved and as typed. It loads when the
  // dialog first opens, and again on the next open if that failed.
  const [savedName, setSavedName] = useState<string>();
  const [nameInput, setNameInput] = useState("");
  useEffect(() => {
    if (!open || savedName !== undefined) return;
    OrganizationsService.getOrganizationNames({
      requestBody: [currently_selected_organization as number],
    })
      .then(([org]) => {
        setSavedName(org.name);
        setNameInput(org.name);
      })
      .catch(() =>
        Notifications.error("Could not load the organization's name.")
      );
  }, [open, savedName, currently_selected_organization]);
  const nameError =
    savedName !== undefined && !nameInput.trim()
      ? "Enter your organization's name."
      : undefined;
  // Both trimmed: a name stored with stray spaces isn't a change.
  const nameChanged =
    savedName !== undefined && nameInput.trim() !== savedName.trim();

  // Loaded the first time the tab is opened.
  const notifications = useNotificationPrefs(
    currently_selected_organization as number,
    tab === "Notifications"
  );

  // The Billing tab's plan loads each time the dialog opens, the booking
  // settings each time their tab opens, and the reply prompt the first time
  // its tab opens after the dialog does. Closing starts them over.
  const paymentPlan = useDefaultPaymentPlan(currently_selected_organization, {
    wanted: open,
  });
  const bookingPages = useBookingPages(
    currently_selected_organization as number,
    open && tab === "Booking pages"
  );
  const replyPrompt = useReplyPrompt(
    currently_selected_organization as number,
    open && tab === "Messaging"
  );

  const settingsChanged = !(
    tutorsCanViewUnassignedStudents ===
      saved.tutors_can_view_unassigned_students &&
    autoReviewAndApproveMeetingsForAdmin ===
      saved.auto_review_and_approve_meetings_for_admin &&
    autoReviewAndApproveMeetingsForTutors ===
      saved.auto_review_and_approve_meetings_for_tutors &&
    requireTutorsToAcceptMeetingsBookedByAdmins ===
      saved.require_tutors_to_accept_meetings_booked_by_admins &&
    requireTutorsToAcceptMeetingsBookedByTutors ===
      saved.require_tutors_to_accept_meetings_booked_by_tutors &&
    requireTutorsToAcceptMeetingsBookedByStudentsOrParents ===
      saved.require_tutors_to_accept_meetings_booked_by_students_or_parents &&
    Number(gracePeriodInput) ===
      saved.grace_period_for_editing_meetings_before_billing_and_payroll &&
    sendPendingApprovalReminderEmails ===
      saved.send_pending_approval_reminder_emails &&
    hideNamesInAvailabilityGrids === saved.hide_names_in_availability_grids
  );

  // Something on some tab is not saved yet.
  const pending =
    nameChanged ||
    settingsChanged ||
    notifications.changed ||
    paymentPlan.changed ||
    bookingPages.changed ||
    replyPrompt.changed;

  /** One Save for every tab: what changed is sent. */
  const updateOrganizationSettings = async () => {
    setStatus(PageStatus.LOADING);
    try {
      if (nameChanged) await saveName();
      if (settingsChanged) await saveSettings();
      if (notifications.changed) await notifications.save();
      if (paymentPlan.changed) await paymentPlan.save();
      if (bookingPages.changed) await bookingPages.save();
      if (replyPrompt.changed) await replyPrompt.save();
      Notifications.success("Organization settings updated successfully");
      setStatus(PageStatus.SUCCESS);
    } catch (err) {
      setError(err as ApiError);
      setStatus(PageStatus.ERROR);
    }
  };

  // The new name shows at once wherever this page shows it.
  const saveName = () =>
    OrganizationsService.updateOrganization({
      orgId: currently_selected_organization as number,
      requestBody: { name: nameInput.trim() },
    }).then((org) => {
      setSavedName(org.name);
      setNameInput(org.name);
      announceOrganizationRenamed({ id: org.id, name: org.name });
    });

  const saveSettings = () =>
    OrganizationsService.updateOrganizationSettings({
      orgId: currently_selected_organization as number,
      requestBody: {
        tutors_can_view_unassigned_students: tutorsCanViewUnassignedStudents,
        auto_review_and_approve_meetings_for_admin:
          autoReviewAndApproveMeetingsForAdmin,
        auto_review_and_approve_meetings_for_tutors:
          autoReviewAndApproveMeetingsForTutors,
        require_tutors_to_accept_meetings_booked_by_admins:
          requireTutorsToAcceptMeetingsBookedByAdmins,
        require_tutors_to_accept_meetings_booked_by_tutors:
          requireTutorsToAcceptMeetingsBookedByTutors,
        require_tutors_to_accept_meetings_booked_by_students_or_parents:
          requireTutorsToAcceptMeetingsBookedByStudentsOrParents,
        grace_period_for_editing_meetings_before_billing_and_payroll:
          Number(gracePeriodInput),
        send_pending_approval_reminder_emails:
          sendPendingApprovalReminderEmails,
        hide_names_in_availability_grids: hideNamesInAvailabilityGrids,
      },
    }).then(setSaved);

  // const [tutorUpForPromotion, setTutorUpForPromotion] =
  //   useState<TutorLimitedResponse>();
  // const [tutor, setTutor] = useState<TutorLimitedResponse>();
  // const [tutors, setTutors] = useState<TutorLimitedResponse[]>([]);
  // const [student, setStudent] = useState<StudentLimitedResponse>();
  // const [students, setStudents] = useState<StudentLimitedResponse[]>([]);
  // const [dontSpamRemoveTutorButton, setDontSpamRemoveTutorButton] =
  //   useState<boolean>(false);
  // const [dontSpamRemoveStudentButton, setDontSpamRemoveStudentButton] =
  //   useState<boolean>(false);
  // const [dontSpamAdminPromotion, setDontSpamAdminPromotion] =
  //   useState<boolean>(false);

  // const handleTutorUpForPromotionChange = (option: any) => {
  //   setTutorUpForPromotion(option);
  // };

  // const handleTutorChange = (option: any) => {
  //   setTutor(option);
  // };

  // const handleStudentChange = (option: any) => {
  //   setStudent(option);
  // };

  // const getAndSetTutors = useCallback(async () => {
  //   TutorsService.getTutorsByOrganization({
  //     orgId: currently_selected_organization as number,
  //   })
  //     .then((tutors) => {
  //       setTutors(tutors);
  //     })
  //     .catch((error) => {
  //       Notifications.error("Could not get tutors");
  //       console.error(`Error (#${error.status}): ${error.message}`);
  //     });
  // }, [currently_selected_organization]);

  // const getAndSetStudents = useCallback(async () => {
  //   StudentsService.getStudentsByOrganizationIfAdmin({
  //     orgId: currently_selected_organization as number,
  //   })
  //     .then((students) => {
  //       setStudents(students);
  //     })
  //     .catch((error) => {
  //       Notifications.error("Could not get students");
  //       console.error(`Error (#${error.status}): ${error.message}`);
  //     });
  // }, [currently_selected_organization]);

  // useEffect(() => {
  //   getAndSetTutors();
  //   getAndSetStudents();
  // }, [getAndSetStudents, getAndSetTutors]);

  useEffect(() => {
    if (error) {
      // A failure with no response (offline) has no body.
      Notifications.error(
        errorText(error, "Not saved. Check your connection and try again.")
      );
    }
  }, [error]);

  // Nothing invalid is ever sent: Save waits until every number is allowed.
  const isDisabled =
    status === PageStatus.LOADING ||
    !!nameError ||
    !!gracePeriodError ||
    notifications.invalid ||
    paymentPlan.invalid ||
    !pending;

  const onClose = () => {
    setStatus(PageStatus.SUCCESS);
    setError(undefined);
    setNameInput(savedName ?? "");
    setTutorsCanViewUnassignedStudents(
      saved.tutors_can_view_unassigned_students
    );
    setAutoReviewAndApproveMeetingsForAdmin(
      saved.auto_review_and_approve_meetings_for_admin
    );
    setAutoReviewAndApproveMeetingsForTutors(
      saved.auto_review_and_approve_meetings_for_tutors
    );
    setRequireTutorsToAcceptMeetingsBookedByAdmins(
      saved.require_tutors_to_accept_meetings_booked_by_admins
    );
    setRequireTutorsToAcceptMeetingsBookedByTutors(
      saved.require_tutors_to_accept_meetings_booked_by_tutors
    );
    setRequireTutorsToAcceptMeetingsBookedByStudentsOrParents(
      saved.require_tutors_to_accept_meetings_booked_by_students_or_parents
    );
    setGracePeriodInput(
      String(saved.grace_period_for_editing_meetings_before_billing_and_payroll)
    );
    setSendPendingApprovalReminderEmails(
      saved.send_pending_approval_reminder_emails
    );
    setHideNamesInAvailabilityGrids(saved.hide_names_in_availability_grids);
    notifications.reset();
    paymentPlan.reset();
    bookingPages.reset();
    replyPrompt.reset();
  };

  return (
    <DialogContent
      onClose={onClose}
      className="dialog-content dialog-content--left wide bg-neutral-50 font-montserrat pb-2 min-h-[85vh] overflow-x-hidden"
      alignLeft={true}
      gradientHeader={true}
    >
      <h1 className="title mb-6">Organization Settings</h1>
      <TabNavbar
        activeTab={tab}
        handleTabClick={setTab}
        options={TABS}
        classes="mb-6 org-settings-tabs"
      />
      {/* Where the autosave status used to be, under the tabs; it stays in
          view while the tab scrolls. */}
      <div
        className="sticky top-0 z-10 -mt-4 mb-2 flex h-6 justify-end"
        aria-live="polite"
      >
        {pending && (
          <p className="flex items-center gap-1 rounded-md bg-neutral-50 px-2 text-sm font-medium text-gray-700">
            Changes pending. Click Save to confirm.
            {/* Save is at the bottom (row-by-row tabs have none). */}
            {!ROW_TABS.includes(tab) && <ArrowDownIcon aria-hidden />}
          </p>
        )}
      </div>

      {/* Every tab stays mounted, so edits survive switching tabs. */}
      <div hidden={tab !== "Scheduling"}>
        <section className={`${SECTION} max-w-3xl`}>
          <h3 className={SECTION_TITLE}>Reviews and approvals</h3>
          <div className="mt-1 divide-y divide-gray-100">
            <YesNoRow
              name="auto-approve-admin"
              label="Auto review and approve meetings for admin"
              value={autoReviewAndApproveMeetingsForAdmin}
              onChange={setAutoReviewAndApproveMeetingsForAdmin}
            />
            <YesNoRow
              name="auto-approve-tutors"
              label="Auto review and approve meetings for tutors"
              value={autoReviewAndApproveMeetingsForTutors}
              onChange={setAutoReviewAndApproveMeetingsForTutors}
            />
          </div>
          <p className={GROUP_LABEL}>Tutors accept their meetings</p>
          <div className="divide-y divide-gray-100">
            <YesNoRow
              name="require-admin"
              label="Require tutors to accept meetings booked by admins"
              value={requireTutorsToAcceptMeetingsBookedByAdmins}
              onChange={setRequireTutorsToAcceptMeetingsBookedByAdmins}
            />
            <YesNoRow
              name="require-tutor"
              label="Require tutors to accept meetings booked by tutors"
              value={requireTutorsToAcceptMeetingsBookedByTutors}
              onChange={setRequireTutorsToAcceptMeetingsBookedByTutors}
            />
            <YesNoRow
              name="require-students"
              label="Require tutors to accept meetings booked by students or parents"
              value={requireTutorsToAcceptMeetingsBookedByStudentsOrParents}
              onChange={
                setRequireTutorsToAcceptMeetingsBookedByStudentsOrParents
              }
            />
          </div>
        </section>

        <section className={`${SECTION} mt-4 max-w-3xl`}>
          <h3 className={SECTION_TITLE}>Availability grids</h3>
          <div className="mt-1">
            <YesNoRow
              name="hide-names-in-availability-grids"
              label="Hide meeting names in the room and tutor availability grids"
              value={hideNamesInAvailabilityGrids}
              onChange={setHideNamesInAvailabilityGrids}
            />
          </div>
        </section>
      </div>

      <div hidden={tab !== "Locations"}>
        <LocationSettings orgId={currently_selected_organization as number} />
      </div>

      <div hidden={tab !== "Billing"}>
        <div className="flex max-w-3xl flex-col gap-4">
          <section className={SECTION}>
            <h3 className={SECTION_TITLE}>Editing grace period</h3>
            {/* One sentence that wraps like text, the box inline. The clock
                starts at the later of the meeting and its last approval. */}
            <p className="mt-2 text-sm leading-6">
              <label htmlFor="grace-period">Meetings stay editable for</label>
              <input
                id="grace-period"
                type="number"
                inputMode="numeric"
                min={MIN_GRACE_DAYS}
                max={MAX_GRACE_DAYS}
                step={1}
                className={COMPACT_INLINE_NUMBER}
                aria-invalid={!!gracePeriodError}
                aria-describedby={
                  gracePeriodError ? "grace-period-error" : undefined
                }
                value={gracePeriodInput}
                onChange={(e) => setGracePeriodInput(e.target.value)}
              />
              {gracePeriodInput.trim() === "1" ? "day" : "days"} after
              they&apos;ve happened and been approved by an admin and every
              tutor. After that, they lock for billing and payroll.
            </p>
            {gracePeriodError && (
              <FieldError id="grace-period-error">
                {gracePeriodError}
              </FieldError>
            )}
          </section>

          <section className={`${SECTION} pb-1`}>
            <DefaultPaymentPlanFields paymentPlan={paymentPlan} />
          </section>
        </div>
      </div>

      <div hidden={tab !== "Subjects"}>
        <SubjectSettings orgId={currently_selected_organization as number} />
      </div>

      <div hidden={tab !== "Booking pages"}>
        <BookingPagesSettings
          orgId={currently_selected_organization as number}
          bookingPages={bookingPages}
        />
      </div>

      <div hidden={tab !== "Notifications"}>
        <NotificationSettings
          prefs={notifications}
          pendingApprovalEmails={sendPendingApprovalReminderEmails}
          onPendingApprovalEmailsChange={setSendPendingApprovalReminderEmails}
        />
      </div>

      <div hidden={tab !== "Messaging"}>
        <MessagingSettings replyPrompt={replyPrompt} />
        <EmailAddressSettings
          orgId={currently_selected_organization as number}
          wanted={open && tab === "Messaging"}
        />
      </div>

      <div hidden={tab !== EMAIL_BRAND_TAB}>
        <EmailBrandSettings wanted={open && tab === EMAIL_BRAND_TAB} />
      </div>

      <div hidden={tab !== "General"}>
        <div className="flex max-w-3xl flex-col gap-4">
          <section className={SECTION}>
            <label htmlFor="org-name" className={SECTION_TITLE}>
              Organization name
            </label>
            <p id="org-name-help" className={SECTION_TEXT}>
              How everyone sees your organization: at the top of Tadpole, on
              your booking pages and forms, and in the emails and texts Tadpole
              sends from now on. Messages already sent keep the old name. Stripe
              receipts and card statements use the business name on your Stripe
              account, so change it there too.
            </p>
            <input
              id="org-name"
              type="text"
              maxLength={MAX_NAME}
              className={`${COMPACT_INPUT} mt-3 w-full max-w-md`}
              value={nameInput}
              disabled={savedName === undefined}
              aria-invalid={!!nameError}
              aria-describedby={
                nameError ? "org-name-help org-name-error" : "org-name-help"
              }
              onChange={(e) => setNameInput(e.target.value)}
            />
            {nameError && (
              <FieldError id="org-name-error">{nameError}</FieldError>
            )}
          </section>

          <section className={SECTION}>
            <h3 className={SECTION_TITLE}>Tutors can</h3>
            <div className="mt-3 flex flex-col gap-2.5">
              <CompactRadio
                id="book-sessions-with-all-students"
                name="tutors-can-view"
                checked={tutorsCanViewUnassignedStudents}
                onChange={() => setTutorsCanViewUnassignedStudents(true)}
              >
                See and book sessions with all students.
              </CompactRadio>
              <CompactRadio
                id="see-sessions-with-paired-students"
                name="tutors-can-view"
                checked={tutorsCanViewUnassignedStudents === false}
                onChange={() => setTutorsCanViewUnassignedStudents(false)}
              >
                Only see and book sessions with students they are paired with.
              </CompactRadio>
            </div>
          </section>
        </div>
      </div>

      {/* The following block remains commented as requested */}
      {/* <div>
        <p className="text-sm font-bold mb-1">Make a Tutor an Admin</p>
        <div className="flex gap-3">
          <div className="w-1/4">
            <Select
              id="tutors-up-for-promotion"
              options={tutors}
              value={tutorUpForPromotion as TutorLimitedResponse}
              getOptionLabel={(t) =>
                t.account.first_name + " " + t.account.last_name
              }
              getOptionValue={(t) => t.account.reference_id}
              placeholder="Tutor..."
              isDisabled={tutors?.length === 0}
              onChange={handleTutorUpForPromotionChange}
            />
          </div>
          <div>
            <Button
              color={
                tutorUpForPromotion === undefined ||
                tutorUpForPromotion === null ||
                dontSpamAdminPromotion
                  ? ButtonColor.GRAY
                  : ButtonColor.GREEN
              }
              disabled={
                tutorUpForPromotion === undefined ||
                tutorUpForPromotion === null ||
                dontSpamAdminPromotion
              }
              onClick={addAccountRole}
              size={ButtonSize.DEFAULT}
            >
              Make Admin
            </Button>
          </div>
        </div>
      </div>
      <div>
        <p className="text-sm font-bold mt-5 mb-1">Remove a Tutor</p>
        <div className="flex gap-3">
          <div className="w-1/4">
            <Select
              id="tutors"
              options={tutors}
              value={tutor as TutorLimitedResponse}
              getOptionLabel={(t) =>
                t.account.first_name + " " + t.account.last_name
              }
              getOptionValue={(t) => t.account.reference_id}
              placeholder="Tutor..."
              isDisabled={tutors?.length === 0}
              onChange={handleTutorChange}
            />
          </div>
          <div>
            <Button
              color={
                tutor === undefined ||
                tutor === null ||
                dontSpamRemoveTutorButton
                  ? ButtonColor.GRAY
                  : ButtonColor.GREEN
              }
              disabled={
                tutor === undefined ||
                tutor === null ||
                dontSpamRemoveTutorButton
              }
              onClick={removeTutor}
              size={ButtonSize.DEFAULT}
            >
              Remove Tutor
            </Button>
          </div>
        </div>
      </div>
      <div>
        <p className="text-sm font-bold mt-5 mb-1">Remove a Student</p>
        <div className="flex gap-3">
          <div className="w-1/4">
            <Select
              id="students"
              options={students}
              value={student as StudentLimitedResponse}
              getOptionLabel={(s) =>
                s.account.first_name + " " + s.account.last_name
              }
              getOptionValue={(s) => s.account.reference_id}
              placeholder="Student..."
              isDisabled={students?.length === 0}
              onChange={handleStudentChange}
            />
          </div>
          <div>
            <Button
              color={
                student === undefined ||
                student === null ||
                dontSpamRemoveStudentButton
                  ? ButtonColor.GRAY
                  : ButtonColor.GREEN
              }
              disabled={
                student === undefined ||
                student === null ||
                dontSpamRemoveStudentButton
              }
              onClick={removeStudent}
              size={ButtonSize.DEFAULT}
            >
              Remove Student
            </Button>
          </div>
        </div>
      </div> */}

      {/* Locations and Subjects save row by row, so they have no Save or
          Cancel (the X still closes the dialog). */}
      {!ROW_TABS.includes(tab) && (
        <DialogActions>
          <div
            className="buttons flex-wrap items-center gap-y-2"
            style={{ marginTop: "30px" }}
          >
            <div className="actionButton">
              <Button
                color={isDisabled ? ButtonColor.GRAY : ButtonColor.GREEN}
                onClick={() => updateOrganizationSettings()}
                disabled={isDisabled}
              >
                Save
              </Button>
            </div>
            <div className="actionButton">
              <DialogClose asChild>
                <Button color={ButtonColor.GRAY} onClick={onClose}>
                  Cancel
                </Button>
              </DialogClose>
            </div>
          </div>
        </DialogActions>
      )}
    </DialogContent>
  );
}
